import * as THREE from "three";
import type { PanoramaSource, PanoramaNode } from "./PanoramaSource.ts";
import type { Pano360Manifest } from "../types.ts";

const PRELOAD_LIMIT = 8;

/**
 * Renders Insta360-style 360° panoramas: a single equirect JPG per node mapped
 * onto a back-side sphere via {@link THREE.MeshBasicMaterial}. The material
 * stays the same across nodes — only `material.map` swaps.
 *
 * Per-node `yaw` from the manifest is applied to the sphere `Object3D` (via
 * {@link getSphereYaw}) so panoramas align across nodes.
 */
export class EquirectSource implements PanoramaSource {
  private nodes_: PanoramaNode[] = [];
  private positions = new Map<number, THREE.Vector3>();
  private images = new Map<number, string>();
  private yaws = new Map<number, number>();
  private adjacency = new Map<number, number[]>();

  private material: THREE.MeshBasicMaterial;
  private texCache = new Map<number, THREE.Texture>();
  private texOrder: number[] = []; // LRU
  private texLoader = new THREE.TextureLoader();

  constructor(
    manifest: Pano360Manifest,
    private imageBaseUrl: string,
    modelOffset: THREE.Vector3,
  ) {
    this.material = new THREE.MeshBasicMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      color: 0x111111, // shown until first texture binds
    });

    for (const node of manifest.nodes) {
      const pos = new THREE.Vector3(
        node.position.x + modelOffset.x,
        node.position.y + modelOffset.y,
        node.position.z + modelOffset.z,
      );
      this.positions.set(node.id, pos);
      this.images.set(node.id, node.image);
      this.yaws.set(node.id, node.yaw ?? 0);
      this.nodes_.push({
        id: node.id,
        position: pos,
        // User's camera starts looking towards world +Z; sphere rotates by yaw.
        initialYaw: 0,
        initialPitch: 0,
      });
    }

    for (const link of manifest.links ?? []) {
      if (!this.adjacency.has(link.from)) this.adjacency.set(link.from, []);
      if (!this.adjacency.has(link.to)) this.adjacency.set(link.to, []);
      this.adjacency.get(link.from)!.push(link.to);
      this.adjacency.get(link.to)!.push(link.from);
    }
  }

  getNodes(): PanoramaNode[] { return this.nodes_; }
  getNeighbors(nodeId: number): number[] { return this.adjacency.get(nodeId) ?? []; }
  getMaterial(): THREE.Material { return this.material; }
  getSphereYaw(nodeId: number): number { return this.yaws.get(nodeId) ?? 0; }

  async loadNode(nodeId: number): Promise<void> {
    const tex = await this._getOrLoad(nodeId);
    if (!tex) return;
    this.material.map = tex;
    this.material.color.setHex(0xffffff);
    this.material.needsUpdate = true;
  }

  preloadNeighbors(nodeId: number): void {
    for (const nid of this.adjacency.get(nodeId) ?? []) {
      if (!this.texCache.has(nid)) void this._getOrLoad(nid);
    }
  }

  /** Update a node's position and/or yaw (used by the alignment UI). */
  updateNode(id: number, patch: { position?: THREE.Vector3; yaw?: number }): void {
    if (patch.position) {
      this.positions.set(id, patch.position.clone());
      const n = this.nodes_.find((n) => n.id === id);
      if (n) n.position.copy(patch.position);
    }
    if (typeof patch.yaw === "number") {
      this.yaws.set(id, patch.yaw);
    }
  }

  dispose(): void {
    this.material.dispose();
    for (const t of this.texCache.values()) t.dispose();
    this.texCache.clear();
    this.texOrder = [];
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private async _getOrLoad(nodeId: number): Promise<THREE.Texture | null> {
    const cached = this.texCache.get(nodeId);
    if (cached) {
      this._touchLru(nodeId);
      return cached;
    }
    const image = this.images.get(nodeId);
    if (!image) return null;

    try {
      const tex = await this.texLoader.loadAsync(`${this.imageBaseUrl}/${encodeURIComponent(image)}`);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.mapping = THREE.EquirectangularReflectionMapping;
      // Equirect on a back-side sphere: flip horizontally so geometry & texture agree.
      tex.wrapS = THREE.RepeatWrapping;
      tex.repeat.x = -1;
      tex.offset.x = 1;
      this.texCache.set(nodeId, tex);
      this.texOrder.push(nodeId);
      this._evictIfNeeded();
      return tex;
    } catch (err) {
      console.warn(`[EquirectSource] failed to load node ${nodeId} (${image}):`, err);
      return null;
    }
  }

  private _touchLru(nodeId: number): void {
    const idx = this.texOrder.indexOf(nodeId);
    if (idx >= 0) this.texOrder.splice(idx, 1);
    this.texOrder.push(nodeId);
  }

  private _evictIfNeeded(): void {
    while (this.texOrder.length > PRELOAD_LIMIT) {
      const evict = this.texOrder.shift()!;
      const tex = this.texCache.get(evict);
      // Don't evict a texture that's currently bound to the material
      if (tex && this.material.map === tex) {
        this.texOrder.push(evict);
        if (this.texOrder.length > PRELOAD_LIMIT * 2) break; // safety
        continue;
      }
      if (tex) tex.dispose();
      this.texCache.delete(evict);
    }
  }
}
